import { createUserRequestSchema, type SignInMethod } from "@procuremate/shared";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { FormError } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/ui/select-field";
import { TextField } from "@/components/ui/text-field";
import { useAuthProviders } from "@/features/auth/api";
import { adminErrorMessage } from "./admin-error";
import { Card } from "./admin-layout";
import { useCreateUser, useOrg, useRoles } from "./api";
import { EMPTY_GRANT, RoleGrantFields, toRoleGrant } from "./role-grant-fields";

export interface InviteResultState {
  invited?: { inviteSent: boolean };
}

/**
 * Creates a user with a first role: a password user is emailed an invite; with Microsoft sign-in
 * configured, a Microsoft user is created without one (ADR-0028).
 */
export function InviteUserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const roles = useRoles();
  const org = useOrg();
  const create = useCreateUser();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [locale, setLocale] = useState<"it" | "en">("en");
  const providers = useAuthProviders();
  const microsoftAvailable = providers.data?.entra === true;
  const [method, setMethod] = useState<SignInMethod>("microsoft");
  const signIn: SignInMethod = microsoftAvailable ? method : "password";
  const [grant, setGrant] = useState(EMPTY_GRANT);
  const [errors, setErrors] = useState<{ email?: string; displayName?: string; role?: string }>({});

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const roleGrant = toRoleGrant(grant, roles.data ?? []);
    const parsed = createUserRequestSchema.safeParse({
      email,
      displayName,
      locale,
      signIn,
      roles: roleGrant ? [roleGrant] : [],
    });
    if (!parsed.success) {
      const fields = z.flattenError(parsed.error).fieldErrors;
      setErrors({
        email: fields.email ? t("auth.invalidEmail") : undefined,
        displayName: fields.displayName ? t("admin.users.nameRequired") : undefined,
        role: roleGrant ? undefined : t("admin.roles.completeRole"),
      });
      return;
    }
    setErrors({});
    create.mutate(parsed.data, {
      onSuccess: (result) =>
        navigate(`/admin/users/${result.user.id}`, {
          state: { invited: { inviteSent: result.inviteSent } } satisfies InviteResultState,
        }),
    });
  }

  // The form depends on whether Microsoft sign-in is configured: wait for it rather than change under the admin.
  if (providers.isPending) {
    return (
      <Card title={t("admin.invite.title")} headingLevel={1}>
        <p role="status">{t("app.loading")}</p>
      </Card>
    );
  }

  return (
    <Card title={t("admin.invite.title")} headingLevel={1}>
      <p className="-mt-2 mb-6 text-sm text-fg-muted">
        {t(signIn === "microsoft" ? "admin.invite.subtitleMicrosoft" : "admin.invite.subtitle")}
      </p>
      <form onSubmit={onSubmit} noValidate className="flex max-w-3xl flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="invite-email"
            label={t("auth.email")}
            type="email"
            autoComplete="off"
            required
            value={email}
            error={errors.email}
            onChange={(event) => {
              setEmail(event.target.value);
              setErrors((e) => ({ ...e, email: undefined }));
            }}
          />
          <TextField
            id="invite-name"
            label={t("admin.users.name")}
            required
            value={displayName}
            error={errors.displayName}
            onChange={(event) => {
              setDisplayName(event.target.value);
              setErrors((e) => ({ ...e, displayName: undefined }));
            }}
          />
        </div>
        {microsoftAvailable ? (
          <SelectField
            id="invite-sign-in"
            className="sm:max-w-xs"
            label={t("admin.users.signInMethod")}
            value={method}
            options={[
              { value: "microsoft", label: t("admin.users.signInMethods.ENTRA") },
              { value: "password", label: t("admin.users.signInMethods.LOCAL") },
            ]}
            onChange={(event) => setMethod(event.target.value as SignInMethod)}
          />
        ) : null}
        <SelectField
          id="invite-locale"
          className="sm:max-w-xs"
          label={t("admin.users.language")}
          value={locale}
          options={[
            { value: "it", label: t("admin.languages.it") },
            { value: "en", label: t("admin.languages.en") },
          ]}
          onChange={(event) => setLocale(event.target.value as "it" | "en")}
        />
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-semibold text-fg">{t("admin.invite.firstRole")}</legend>
          <RoleGrantFields
            idPrefix="invite"
            value={grant}
            onChange={(draft) => {
              setGrant(draft);
              setErrors((e) => ({ ...e, role: undefined }));
            }}
            roles={roles.data ?? []}
            org={org.data}
            error={errors.role}
          />
        </fieldset>
        <FormError message={adminErrorMessage(create.error, t, "admin.errors.emailExists")} />
        <div className="mt-2 flex flex-wrap gap-3">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending
              ? t("auth.sending")
              : t(signIn === "microsoft" ? "admin.invite.submitMicrosoft" : "admin.invite.submit")}
          </Button>
          <Link
            to="/admin/users"
            className="inline-flex h-10 items-center rounded-md px-4 text-sm font-semibold text-primary hover:bg-bg"
          >
            {t("admin.cancel")}
          </Link>
        </div>
      </form>
    </Card>
  );
}
